import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { MarketplacePropertyCategory_ListingVideoPlaybackEvent_Milestone as PlaybackMilestone } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { useListingExperiment } from 'components/marketplace/helpers/useListingExperiment';
import { useObservability } from 'helpers/observability';

import type { ListingMediaAvailability } from './mediaSlot';
import { resolveMediaSlotClaim, resolveMediaState } from './mediaSlot';

type PlaybackPosition = {
  positionSeconds?: number;
  durationSeconds?: number;
};

type UsePdpMediaSlotOptions = {
  addressToken: string;
  availability: ListingMediaAvailability;
  /** Where the slot renders; impressions and exposures are deduped per (listing, surface). */
  surface?: 'pdp' | 'map-drawer';
};

export type PdpMediaSlot = {
  /** State the slot is showing, including a viewer's toggle away from their arm. */
  mediaState: ListingMediaState;
  /** State the assigned arm renders, which the toggle returns to. */
  assignedState: ListingMediaState;
  /** Moves the slot to another state and records the move. */
  selectMediaState: (next: ListingMediaState) => void;
  /** Records a playback milestone, at most once per playback session. */
  trackPlaybackMilestone: (milestone: PlaybackMilestone, position?: PlaybackPosition) => void;
  /**
   * Begins a new playback session, so milestones already recorded count again.
   * A viewer can reach a fresh playback without the slot changing state --
   * retrying after a failure, or pressing play on the breakpoint twin -- and
   * that view would otherwise be deduped against the session it did not share.
   */
  startPlaybackSession: () => void;
};

/**
 * Listings already exposed, keyed by experiment and listing.
 *
 * Module-scoped to match the assignment cache in `useListingExperiment`, for
 * the same reason: the listing is the unit of randomisation, so a viewer
 * returning to a listing they already saw is the same unit on the same
 * listing. A ref re-arms on that return and bills a second exposure against
 * one unit, which inflates the denominator the readout divides by.
 */
const exposedListings = new Set<string>();
const impressedListings = new Set<string>();

/** Exported for tests; the sets are otherwise page-session lifetime. */
export const __resetPdpMediaExposures = () => {
  exposedListings.clear();
  impressedListings.clear();
};

type Selection = { addressToken: string; state: ListingMediaState };
type PlaybackSession = { addressToken: string; fired: Set<PlaybackMilestone> };

/**
 * Resolves which media the PDP media slot shows for a listing, and owns the
 * slot's instrumentation.
 *
 * Everything a viewer accumulates here — their toggle, their playback progress
 * — is scoped to the listing that produced it, because the PDP route swaps the
 * listing under a mounted component.
 */
export const usePdpMediaSlot = ({
  addressToken,
  availability,
  surface = 'pdp',
}: UsePdpMediaSlotOptions): PdpMediaSlot => {
  const { trackJourneyEvent } = useObservability();
  const claim = useMemo(() => resolveMediaSlotClaim(availability), [availability]);
  const assignment = useListingExperiment({
    experimentKey: claim?.experimentKey ?? '',
    addressId: addressToken,
    // Derived from the key itself rather than from `claim` again: the platform
    // rejects an empty key outright, so two expressions that have to agree for
    // that never to be sent are one edit away from disagreeing.
    enabled: Boolean(claim?.experimentKey),
  });

  const [selection, setSelection] = useState<Selection | null>(null);
  const assignedState =
    !claim && availability.hasTour3d
      ? ListingMediaState.TOUR_3D
      : resolveMediaState(claim, assignment.arm);
  const mediaState = selection?.addressToken === addressToken ? selection.state : assignedState;

  const playback = useRef<PlaybackSession>({ addressToken, fired: new Set() });

  const startPlaybackSession = useCallback(() => {
    playback.current = { addressToken, fired: new Set() };
  }, [addressToken]);

  // Only an enrolled listing impresses. The event is an experiment's
  // denominator, so firing it on every PDP view would bury the arms it exists
  // to compare in the views that are in no experiment at all.
  useEffect(() => {
    // Module-scoped like the exposure set below: the /homes map drawer mounts
    // a desktop and a mobile slot for the same listing (one hidden with CSS),
    // and a per-instance ref would record the hidden twin's impression too.
    const impressionKey = `${addressToken}|${surface}`;
    if (!claim || assignment.arm == null || impressedListings.has(impressionKey)) {
      return;
    }
    impressedListings.add(impressionKey);
    trackJourneyEvent('marketplaceProperty', 'listingMediaImpression', {
      addressUuid: addressToken,
      mediaState: assignedState,
      experimentKey: claim.experimentKey,
      variant: assignment.arm,
    });
    // The exposure the GrowthBook assignment query reads, which joins on
    // `address_token`. The index gates it: that query filters
    // `variation_id IS NOT NULL`, so an exposure without one is not a degraded
    // row but no row, and its arm loses the whole population.
    const exposureKey = `${claim.experimentKey}|${addressToken}|${surface}`;
    if (assignment.variationId != null && !exposedListings.has(exposureKey)) {
      exposedListings.add(exposureKey);
      trackJourneyEvent('experimentation', 'impression', {
        experimentName: claim.experimentKey,
        variant: assignment.arm,
        variationId: assignment.variationId,
        addressToken,
      });
    }
  }, [
    addressToken,
    assignedState,
    assignment.arm,
    assignment.variationId,
    claim,
    surface,
    trackJourneyEvent,
  ]);

  const selectMediaState = useCallback(
    (next: ListingMediaState) => {
      if (next === mediaState) {
        return;
      }
      // Leaving the video ends its playback session, so the next play start is
      // a new one and its milestones count again.
      startPlaybackSession();
      setSelection({ addressToken, state: next });
      trackJourneyEvent('marketplaceProperty', 'listingMediaToggle', {
        addressUuid: addressToken,
        fromState: mediaState,
        toState: next,
      });
    },
    [addressToken, mediaState, startPlaybackSession, trackJourneyEvent],
  );

  const trackPlaybackMilestone = useCallback(
    (milestone: PlaybackMilestone, position: PlaybackPosition = {}) => {
      if (playback.current.addressToken !== addressToken) {
        startPlaybackSession();
      } else if (milestone === PlaybackMilestone.PLAY_START) {
        // A `<video>` fires `play` on every unpause, so a play start is only a
        // new session after the last one finished. Counting unpauses would put
        // play rate above one play per impression.
        if (playback.current.fired.has(PlaybackMilestone.COMPLETE)) {
          startPlaybackSession();
        } else if (playback.current.fired.has(milestone)) {
          return;
        }
      } else if (playback.current.fired.has(milestone)) {
        return;
      }
      playback.current.fired.add(milestone);
      trackJourneyEvent('marketplaceProperty', 'listingVideoPlayback', {
        addressUuid: addressToken,
        milestone,
        ...(position.positionSeconds != null && { positionSeconds: position.positionSeconds }),
        ...(position.durationSeconds != null && { durationSeconds: position.durationSeconds }),
      });
    },
    [addressToken, startPlaybackSession, trackJourneyEvent],
  );

  // Memoised because consumers key `useCallback` and `useMemo` on this object;
  // a fresh literal each render invalidates all of them.
  return useMemo(
    () => ({
      mediaState,
      assignedState,
      selectMediaState,
      trackPlaybackMilestone,
      startPlaybackSession,
    }),
    [mediaState, assignedState, selectMediaState, trackPlaybackMilestone, startPlaybackSession],
  );
};
